import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
	Trophy,
	Play,
	Loader2,
	Flame,
	Star,
	Zap,
	TrendingUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/utils/cn";
import { testGamification } from "@/services/simulation";
import { useToast } from "@/components/ui/use-toast";

export function GamificationTester() {
	const { toast } = useToast();
	const [formData, setFormData] = useState({
		currentStreak: 5,
		productivityScore: 75,
		focusHours: 6,
		deepFocusSessions: 3,
		totalXP: 2500,
		level: 5,
	});

	const gamificationMutation = useMutation({
		mutationFn: testGamification,
		onError: (error: any) => {
			toast({
				title: "Error",
				description: error.message || "Failed to calculate gamification",
				variant: "destructive",
			});
		},
	});

	const handleSubmit = () => {
		gamificationMutation.mutate(formData);
	};

	const result = gamificationMutation.data;

	// Calculate progress to next level
	const calculateLevelProgress = (xp: number, level: number) => {
		let xpRequired = 100;
		for (let i = 1; i < level; i++) {
			xpRequired = Math.floor(xpRequired * 1.2);
		}
		const nextLevelXP = Math.floor(xpRequired * 1.2);
		const currentLevelStart = xpRequired;
		const xpInCurrentLevel = xp - currentLevelStart;
		const xpNeeded = nextLevelXP - currentLevelStart;
		return Math.min(100, Math.round((xpInCurrentLevel / xpNeeded) * 100));
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Trophy className="w-6 h-6" />
						Gamification Tester
					</h1>
					<p className="text-gray-500 mt-1">
						Test XP calculation, streaks, and level progression
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Input Form */}
				<Card>
					<CardHeader>
						<CardTitle>Current State</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Current Streak */}
						<div>
							<div className="flex items-center justify-between mb-2">
								<Label className="flex items-center gap-2">
									<Flame className="w-4 h-4 text-orange-500" />
									Current Streak
								</Label>
								<Badge variant="outline" className="text-orange-600">
									{formData.currentStreak} days
								</Badge>
							</div>
							<Slider
								value={[formData.currentStreak]}
								onValueChange={(values: number[]) =>
									setFormData({ ...formData, currentStreak: values[0] })
								}
								max={30}
								step={1}
							/>
						</div>

						{/* Productivity Score */}
						<div>
							<div className="flex items-center justify-between mb-2">
								<Label className="flex items-center gap-2">
									<TrendingUp className="w-4 h-4 text-blue-500" />
									Productivity Score
								</Label>
								<Badge variant="outline">{formData.productivityScore}</Badge>
							</div>
							<Slider
								value={[formData.productivityScore]}
								onValueChange={(values: number[]) =>
									setFormData({ ...formData, productivityScore: values[0] })
								}
								max={100}
								step={1}
							/>
						</div>

						{/* Focus Hours */}
						<div>
							<div className="flex items-center justify-between mb-2">
								<Label>Focus Hours (today)</Label>
								<Badge variant="outline">{formData.focusHours}h</Badge>
							</div>
							<Slider
								value={[formData.focusHours]}
								onValueChange={(values: number[]) =>
									setFormData({ ...formData, focusHours: values[0] })
								}
								max={12}
								step={0.5}
							/>
						</div>

						{/* Deep Focus Sessions */}
						<div>
							<div className="flex items-center justify-between mb-2">
								<Label className="flex items-center gap-2">
									<Zap className="w-4 h-4 text-purple-500" />
									Deep Focus Sessions
								</Label>
								<Badge variant="outline">{formData.deepFocusSessions}</Badge>
							</div>
							<Slider
								value={[formData.deepFocusSessions]}
								onValueChange={(values: number[]) =>
									setFormData({ ...formData, deepFocusSessions: values[0] })
								}
								max={10}
								step={1}
							/>
						</div>

						{/* Current XP and Level */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label>Total XP</Label>
								<Input
									type="number"
									value={formData.totalXP}
									onChange={(e) =>
										setFormData({ ...formData, totalXP: Number(e.target.value) })
									}
									className="mt-2"
								/>
							</div>
							<div>
								<Label>Current Level</Label>
								<Input
									type="number"
									value={formData.level}
									onChange={(e) =>
										setFormData({ ...formData, level: Number(e.target.value) })
									}
									min={1}
									max={100}
									className="mt-2"
								/>
							</div>
						</div>

						{/* Current State Preview */}
						<div className="p-4 bg-gray-50 rounded-lg">
							<div className="flex items-center justify-between mb-2">
								<span className="text-sm text-gray-600">Current Level Progress</span>
								<span className="text-sm font-medium">
									{calculateLevelProgress(formData.totalXP, formData.level)}%
								</span>
							</div>
							<div className="w-full bg-gray-200 rounded-full h-3">
								<div
									className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-blue-500"
									style={{ width: `${calculateLevelProgress(formData.totalXP, formData.level)}%` }}
								/>
							</div>
						</div>

						{/* Calculate Button */}
						<Button
							className="w-full"
							size="lg"
							onClick={handleSubmit}
							disabled={gamificationMutation.isPending}
						>
							{gamificationMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Play className="w-4 h-4 mr-2" />
							)}
							Calculate XP Earned
						</Button>
					</CardContent>
				</Card>

				{/* Results */}
				<Card>
					<CardHeader>
						<CardTitle>Result</CardTitle>
					</CardHeader>
					<CardContent>
						{result ? (
							<div className="space-y-6">
								{/* XP Earned */}
								<div className="text-center p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
									<p className="text-sm text-gray-600 mb-2">XP Earned</p>
									<p className="text-5xl font-bold text-purple-600">
										+{result.xpEarned}
									</p>
									{result.leveledUp && (
										<div className="mt-3 flex items-center justify-center gap-2">
											<Star className="w-5 h-5 text-amber-400 fill-amber-400" />
											<Badge className="bg-amber-500">LEVEL UP!</Badge>
											<Star className="w-5 h-5 text-amber-400 fill-amber-400" />
										</div>
									)}
								</div>

								{/* New State */}
								<div className="grid grid-cols-2 gap-4">
									<div className="p-4 bg-gray-50 rounded-lg text-center">
										<p className="text-sm text-gray-600">New Total XP</p>
										<p className="text-2xl font-bold text-gray-900">
											{result.newTotalXP.toLocaleString()}
										</p>
									</div>
									<div className="p-4 bg-gray-50 rounded-lg text-center">
										<p className="text-sm text-gray-600">Level</p>
										<p className={cn(
											"text-2xl font-bold",
											result.leveledUp ? "text-amber-600" : "text-gray-900"
										)}>
											{result.newLevel}
										</p>
									</div>
								</div>

								{/* Streak Update */}
								<div className="p-4 bg-orange-50 rounded-lg">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											<Flame className={cn(
												"w-6 h-6",
												result.streakUpdate.active ? "text-orange-500" : "text-gray-400"
											)} />
											<span className="font-medium">Streak</span>
										</div>
										<div className="text-right">
											<p className="text-2xl font-bold text-orange-600">
												{result.streakUpdate.newStreak} days
											</p>
											<p className="text-sm text-gray-600">
												{result.streakUpdate.active ? "Active!" : "Broken"}
											</p>
										</div>
									</div>
								</div>

								{/* XP Breakdown */}
								<div>
									<h4 className="font-medium text-gray-700 mb-3">XP Breakdown</h4>
									<div className="space-y-2">
										<div className="flex items-center justify-between p-2 bg-gray-50 rounded">
											<span className="text-sm">Base XP (Focus Hours)</span>
											<Badge variant="outline">+{result.breakdown.baseXP}</Badge>
										</div>
										<div className="flex items-center justify-between p-2 bg-purple-50 rounded">
											<span className="text-sm">Deep Focus Bonus</span>
											<Badge variant="outline" className="text-purple-600">
												+{result.breakdown.deepFocusBonus}
											</Badge>
										</div>
										<div className="flex items-center justify-between p-2 bg-orange-50 rounded">
											<span className="text-sm">Streak Multiplier</span>
											<Badge variant="outline" className="text-orange-600">
												x{result.breakdown.streakMultiplier.toFixed(2)}
											</Badge>
										</div>
										<div className="flex items-center justify-between p-2 bg-blue-50 rounded">
											<span className="text-sm">Productivity Bonus</span>
											<Badge variant="outline" className="text-blue-600">
												+{result.breakdown.productivityBonus}
											</Badge>
										</div>
									</div>
								</div>
							</div>
						) : (
							<div className="text-center py-12 text-gray-500">
								<Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Set the current state and click Calculate to see results</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
